/*
 * grid-background.js — Sfondo a griglia procedurale (Offporter)
 *
 * Righe orizzontali dinamiche (cadono sui bordi delle zone reali, ricalcolate responsive) +
 * tilt 3D e reveal spotlight gestiti da un solo pointermove e un solo RAF.
 * IIFE + guard DOMContentLoaded, RAF-throttled, nessuna dipendenza.
 */
(function () {
  'use strict';

  /* Ancore strutturali: per ognuna presente e renderizzata, i bordi top + bottom
     diventano righe orizzontali. Un elemento può anche marcarsi con
     data-grid-rule="top|bottom|both" per aggiungere una riga ai suoi bordi. */
  var ANCHORS = [
    '.off-header',
    '.off-zone--top',
    '.off-zone--middle',
    '.off-zone--bottom',
    '.off-footer',
  ];

  var TILT_EASE = 0.12;

  function lerp(current, target, factor) {
    return current + (target - current) * factor;
  }

  function init() {
    var bg = document.querySelector('[data-grid-bg]');
    if (!bg) return;
    // Layer nascosto (es. su mobile con hide-mobile attivo) → niente calcolo righe/mouse a vuoto.
    // NB: sulle pagine con video la griglia ora è VISIBILE sopra il bg (tilt + reveal attivi).
    if (window.getComputedStyle(bg).display === 'none') return;
    var gridEl = bg.querySelector('[data-grid-bg-grid]');
    var revealEl = bg.querySelector('[data-grid-bg-reveal]');
    var rulesEl = bg.querySelector('[data-grid-bg-rules]');

    if (rulesEl) initHorizontalRules(bg, rulesEl);
    if (gridEl) initMouse(bg, gridEl, revealEl);
  }

  /* ---- Righe orizzontali dinamiche ---- */
  function initHorizontalRules(bg, rulesEl) {
    var pool = []; // div riga riusati (niente thrash del DOM)
    var raf = 0;

    // rect dell'elemento, o null se non renderizzato (display:contents / rect vuoto).
    function visibleRect(el) {
      var r = el.getBoundingClientRect();
      return r.height < 1 && r.width < 1 ? null : r;
    }

    function collectYs() {
      var ys = [];
      var i, r;

      for (i = 0; i < ANCHORS.length; i++) {
        var el = document.querySelector(ANCHORS[i]);
        if (!el) continue;
        r = visibleRect(el);
        if (!r) continue;
        ys.push(r.top, r.bottom);
      }

      var marked = document.querySelectorAll('[data-grid-rule]');
      for (i = 0; i < marked.length; i++) {
        r = visibleRect(marked[i]);
        if (!r) continue;
        var edge = marked[i].getAttribute('data-grid-rule');
        if (edge === 'bottom') ys.push(r.bottom);
        else if (edge === 'both') ys.push(r.top, r.bottom);
        else ys.push(r.top);
      }

      // ordina, arrotonda, scarta i quasi-duplicati (< 2px)
      ys.sort(function (a, b) { return a - b; });
      var out = [];
      for (i = 0; i < ys.length; i++) {
        var y = Math.round(ys[i] * 10) / 10;
        if (out.length === 0 || Math.abs(y - out[out.length - 1]) >= 2) out.push(y);
      }
      return out;
    }

    function render() {
      raf = 0;
      var ys = collectYs();
      var i, el;

      for (i = 0; i < ys.length; i++) {
        el = pool[i];
        if (!el) {
          el = document.createElement('div');
          el.className = 'off-grid-bg__rule';
          rulesEl.appendChild(el);
          pool[i] = el;
        }
        el.style.top = ys[i] + 'px';
        el.style.display = '';
      }
      // nascondi i div in eccesso (rimangono nel pool per riuso)
      for (i = ys.length; i < pool.length; i++) {
        pool[i].style.display = 'none';
      }
    }

    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(render);
    }

    schedule();
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(schedule).observe(document.body);
    }
    if (document.fonts?.ready) {
      document.fonts.ready.then(schedule).catch(function () {});
    }
    // ricalcolo di sicurezza dopo l'intro/animazioni d'ingresso
    setTimeout(schedule, 1200);
  }

  /* ---- Interazione mouse: tilt 3D + reveal spotlight ----
     Un solo pointermove e un solo RAF muovono entrambi gli effetti. Il tilt ruota il layer
     griglia; il reveal sposta lo spotlight (--gridbg-mx/--gridbg-my) sul wrapper PIATTO, che
     porta la mask separata dal layer che si inclina (niente flicker da desync compositor).
     Lerp verso il target = lag/momentum; il RAF si ferma a convergenza e riparte al movimento.
     Disattivi su touch e reduced-motion (lì il CSS mostra la griglia statica intera). */
  function initMouse(bg, gridEl, revealEl) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(pointer: coarse)').matches) return; // touch → niente mouse

    var maxA = parseFloat(bg.getAttribute('data-tilt-max'));
    var tiltOn = bg.getAttribute('data-tilt') === 'true' && maxA > 0;
    var revealOn = bg.getAttribute('data-grid-reveal') === 'true' && !!revealEl;
    if (!tiltOn && !revealOn) return;

    var follow = parseFloat(bg.getAttribute('data-reveal-follow')) / 100;
    if (!(follow > 0)) follow = 0.16;

    var targetRX = 0, targetRY = 0, curRX = 0, curRY = 0;    // tilt: target/current (gradi)
    var tmx = window.innerWidth / 2, tmy = window.innerHeight / 2, cmx = tmx, cmy = tmy; // reveal (px)
    var running = false;

    function loop() {
      var more = false;
      if (tiltOn) {
        curRX = lerp(curRX, targetRX, TILT_EASE);
        curRY = lerp(curRY, targetRY, TILT_EASE);
        gridEl.style.setProperty('--rx', curRX.toFixed(3) + 'deg');
        gridEl.style.setProperty('--ry', curRY.toFixed(3) + 'deg');
        if (Math.abs(targetRX - curRX) > 0.01 || Math.abs(targetRY - curRY) > 0.01) more = true;
      }
      if (revealOn) {
        cmx = lerp(cmx, tmx, follow);
        cmy = lerp(cmy, tmy, follow);
        revealEl.style.setProperty('--gridbg-mx', cmx.toFixed(1) + 'px');
        revealEl.style.setProperty('--gridbg-my', cmy.toFixed(1) + 'px');
        if (Math.abs(tmx - cmx) > 0.5 || Math.abs(tmy - cmy) > 0.5) more = true;
      }
      if (more) {
        requestAnimationFrame(loop);
      } else {
        running = false;
      }
    }

    function onMove(e) {
      if (tiltOn) {
        var mx = e.clientX / window.innerWidth;  // 0..1
        var my = e.clientY / window.innerHeight; // 0..1
        targetRX = (my - 0.5) * 2 * maxA;   // rotateX: mouse in basso → inclina avanti
        targetRY = (0.5 - mx) * 2 * maxA;   // rotateY: mouse a destra → inclina a destra
      }
      if (revealOn) {
        tmx = e.clientX;
        tmy = e.clientY;
      }
      if (!running) {
        running = true;
        requestAnimationFrame(loop);
      }
    }

    window.addEventListener('pointermove', onMove, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
